import React, { useState } from 'react';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faSpinner } from '@/icons';
import { useTheme } from '../contexts/ThemeContext';
import { useAlert } from '../contexts/AlertContext';
import { caseApiService } from '../services/caseApiService';
import { ProDataReasonsNotice } from './ProDataReasonsNotice';
import type { NonSplitCustodyArrangement } from '../utils/proDataReasons';
import type { CaseSummary } from '../types/caseApi';
import { useSingleFlight } from '../hooks/useSingleFlight';
import { isCopyInProgressError, copyInProgressMessage } from '../utils/copyInProgress';
import { debugError } from '../utils/debug';
import { useSystemSettingsContext } from '../contexts/SystemSettingsContext';
import { useHasAnyRole } from '../contexts/AuthContext';
import { getTaxYearOptions, isCalcVersionDisabledError } from '../utils/calcVersionAvailability';

interface CopyCaseModalProps {
    /** The case to copy. */
    caseId: string;
    /** Its case-list row: tax year and party names. */
    caseToCopy?: CaseSummary;
    /** ProDowngrade: the copy strips this case's Pro-only data. Say so, and ask for custody when split. */
    willStripProData: boolean;
    /** HomePage's styles object (modal and button styles). */
    styles: any;
    onClose: () => void;
    /** The copy was made. The caller opens it. */
    onCopied: (newCaseId: string) => void | Promise<void>;
}

/**
 * HomePage "Copy Case" dialog. The dialog is mounted once per opening, so each opening is a new
 * user action with its own request id; a double click, a repeated Enter or a retry after an error
 * reuses it, and the server returns the same copy for it. The confirm button is disabled with a
 * spinner from the first click until the request settles. See docs/AUTOSAVE_DETAILS.md
 * "Copy idempotency".
 */
export const CopyCaseModal: React.FC<CopyCaseModalProps> = ({
    caseId,
    caseToCopy,
    willStripProData,
    styles,
    onClose,
    onCopied,
}) => {
    const theme = useTheme();
    const { addAlert } = useAlert();
    const [selectedTaxYear, setSelectedTaxYear] = useState<number | null>(null);
    // ProDowngrade: the arrangement that replaces split custody on a converted copy.
    const [custodyChoice, setCustodyChoice] = useState<NonSplitCustodyArrangement | null>(null);
    const [needsCustodyChoice, setNeedsCustodyChoice] = useState(false);
    const copyFlight = useSingleFlight();
    const copying = copyFlight.inFlight;

    const { calcVersions } = useSystemSettingsContext();
    const isAdminOrRoot = useHasAnyRole(['Admin', 'Root']);
    const caseTaxYear = caseToCopy?.txyr;
    const caseStateCode = caseToCopy?.sc || 'IA';
    const isCaseOlderThan2025 = caseTaxYear !== undefined && caseTaxYear < 2025;
    // A split case is copied only once the user has chosen the arrangement to use instead.
    const copyBlocked = copying
        || (isCaseOlderThan2025 && !selectedTaxYear)
        || (willStripProData && needsCustodyChoice && !custodyChoice);

    // Built tax years: enabled ones, plus turned-off ones tagged "Not live" for admins.
    const taxYearOptions = getTaxYearOptions(caseStateCode, calcVersions, isAdminOrRoot);

    const close = () => {
        if (!copying) onClose();
    };

    const handleCopy = () => {
        if (copyBlocked) return;
        void copyFlight.run(async (requestId) => {
            try {
                // Pass the new tax year if provided (for cases older than 2025)
                const newTaxYear = isCaseOlderThan2025 ? selectedTaxYear : undefined;
                const response = await caseApiService.copyCase(
                    caseId,
                    newTaxYear ?? undefined,
                    willStripProData && custodyChoice ? custodyChoice : undefined,
                    requestId,
                );
                if (!response.success || !response.newCaseId) {
                    throw new Error(response.message || 'Failed to copy case');
                }
                addAlert('success', 'Case copied successfully');
                await onCopied(response.newCaseId);
            } catch (error) {
                if (isCopyInProgressError(error)) {
                    addAlert('info', copyInProgressMessage(error));
                } else if (isCalcVersionDisabledError(error)) {
                    addAlert('error', error instanceof Error && error.message ? error.message : 'That tax year is not available yet.');
                } else {
                    debugError('Error copying case:', error);
                    addAlert('error', 'Failed to copy case. Please try again.');
                }
                // Rethrown so a retry keeps this action's request id.
                throw error;
            }
        }).catch(() => {});
    };

    return (
        <div style={styles.modalOverlay} onClick={close}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h2 style={styles.modalTitle}>Copy Case</h2>
                    <button style={styles.modalCloseButton} onClick={close} disabled={copying}>
                        ×
                    </button>
                </div>
                <div style={styles.modalBody}>
                    {willStripProData && (
                        <div style={{
                            display: 'flex',
                            gap: theme.spacing.sm,
                            padding: theme.spacing.sm,
                            marginBottom: theme.spacing.md,
                            borderRadius: theme.borderRadius.sm,
                            border: `1px solid ${theme.colors.status.warning}`,
                            backgroundColor: theme.colors.status.warning + '15',
                        }}>
                            <FontAwesomeIcon
                                icon={faExclamationTriangle}
                                style={{ color: theme.colors.status.warning, marginTop: '2px' }}
                            />
                            <ProDataReasonsNotice
                                caseId={caseId}
                                fontSize={theme.typography.fontSize.sm}
                                intro="This case uses Pro features and your team no longer has Pro. The copy will not include:"
                                custodyChoice={custodyChoice}
                                onCustodyChoiceChange={setCustodyChoice}
                                onNeedsCustodyChoiceChange={setNeedsCustodyChoice}
                                partyNames={{ partyA: caseToCopy?.afn, partyB: caseToCopy?.bfn }}
                            />
                        </div>
                    )}
                    {isCaseOlderThan2025 ? (
                        <>
                            <p style={styles.modalText}>
                                This case is from {caseTaxYear}, which is older than 2025.
                                When copying, you must select a tax year that is 2025 or newer.
                            </p>
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: theme.colors.text.primary || '#000000',
                                }}>
                                    Select Tax Year *
                                </label>
                                <Select
                                    value={taxYearOptions.find(opt => opt.value === selectedTaxYear) || null}
                                    onChange={(selectedOption) => {
                                        if (selectedOption) {
                                            setSelectedTaxYear(selectedOption.value);
                                        }
                                    }}
                                    options={taxYearOptions}
                                    placeholder="Select tax year..."
                                    isDisabled={copying}
                                    styles={{
                                        control: (provided) => ({
                                            ...provided,
                                            fontSize: '14px',
                                            minHeight: '38px',
                                        }),
                                    }}
                                />
                            </div>
                        </>
                    ) : (
                        <p style={styles.modalText}>Are you sure you want to copy this case? A new case will be created with all the data from this case.</p>
                    )}
                </div>
                <div style={styles.modalFooter}>
                    <button
                        style={{
                            ...styles.modalButton,
                            backgroundColor: (theme.colors.background as any)?.secondary || '#f3f4f6',
                            color: theme.colors.text.primary || '#000000',
                        }}
                        onClick={close}
                        disabled={copying}
                    >
                        Cancel
                    </button>
                    <button
                        style={{
                            ...styles.modalButton,
                            backgroundColor: theme.colors.primary || '#3b82f6',
                            color: '#ffffff',
                            opacity: copyBlocked ? 0.5 : 1,
                            cursor: copyBlocked ? 'not-allowed' : 'pointer',
                        }}
                        disabled={copyBlocked}
                        aria-busy={copying}
                        data-testid="copy-case-confirm"
                        onClick={handleCopy}
                    >
                        {copying && <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: theme.spacing.xs }} />}
                        {copying ? 'Copying…' : willStripProData ? 'Copy Without Pro Data' : 'Copy Case'}
                    </button>
                </div>
            </div>
        </div>
    );
};
